import dayjs from "dayjs";
import { CalendarContentProps } from "../../routes/scheduledJob/_component/Calendar.types";
import { defaultRequestConfig, useMutation, useRequest } from "../../utils/api";
import {
  MutationConfigType,
  RequestConfigType,
  ShortMutationConfigType,
} from "../../utils/api.type";
import {
  DeleteOneAndFollowingProps,
  DeleteOneEventProps,
  EditDeliveryFormProps,
  EditFormPayload,
  EditFormProps,
  ListScheduleCleaningProjection,
  StoreScheduleJobProps,
} from "./index.type";
import { useEffect, useState } from "react";

const GET_RECURRING_JOB = "/v1/rms/recurring_job/projection/";
const GET_RECURRING_CLEANING_JOB =
  "/v1/organisations/:organisation_id/cleaning_schedule/projection/";
interface RecurringJobProps extends RequestConfigType {
  start_time?: Date;
  end_time?: Date;
}

export const useScheduledJobs = (obj = {} as RecurringJobProps) => {
  return useRequest<CalendarContentProps[]>(
    GET_RECURRING_JOB,
    {
      ...obj,
      start_time: obj.start_time ? dayjs(obj.start_time).unix() : undefined,
      end_time: obj.end_time ? dayjs(obj.end_time).unix() : undefined,
    },
    {
      enabled: obj.enabled,
    },
  );
};

export const useScheduledCleaningJobs = (
  obj = {} as RecurringJobProps & { organisation_id: string },
) => {
  return useRequest<ListScheduleCleaningProjection[]>(
    GET_RECURRING_CLEANING_JOB.replace(":organisation_id", obj.organisation_id),
    {
      // ...obj,
      start_time: obj.start_time ? dayjs(obj.start_time).unix() : undefined,
      end_time: obj.end_time ? dayjs(obj.end_time).unix() : undefined,
    },
    {
      enabled: obj.enabled,
    },
  );
};

const STORE_RECURRING_JOB = "/v1/rms/recurring_job/";
export const useCreateScheduledJob = (options: ShortMutationConfigType) => {
  return useMutation<StoreScheduleJobProps, any>(STORE_RECURRING_JOB, options);
};

const STORE_RECURRING_CLEANING_JOB =
  "/v1/organisations/:organisation_id/cleaning_schedule/";
export const useCreateScheduledCleaningJob = (
  options: ShortMutationConfigType & { organisation_id: string },
) => {
  return useMutation<StoreScheduleJobProps, any>(
    STORE_RECURRING_CLEANING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ),
    options,
  );
};

const DELETE_RECURRING_CLEANING_JOB =
  "/v1/organisations/:organisation_id/cleaning_schedule/:task_id/";
export const useDeleteAllScheduledCleaningJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    taskId: string;
  },
) => {
  return useMutation<StoreScheduleJobProps, any>(
    DELETE_RECURRING_CLEANING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ).replace(":task_id", options.taskId),
    {
      ...options,
      method: "DELETE",
    },
  );
};

const DELETE_RECURRING_JOB =
  "/v1/rms/recurring_job/:task_id/";
export const useDeleteAllScheduledJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    taskId: string;
  },
) => {
  return useMutation<StoreScheduleJobProps, any>(
    DELETE_RECURRING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ).replace(":task_id", options.taskId),
    {
      ...options,
      method: "DELETE",
    },
  );
};

const DELETE_ONE_RECURRING_CLEANING_JOB =
  "/v1/organisations/:organisation_id/cleaning_schedule/remove_job_date/";
export const useDeleteOneScheduledCleaningJob = (
  options: ShortMutationConfigType & { organisation_id: string },
) => {
  return useMutation<any, DeleteOneEventProps>(
    DELETE_ONE_RECURRING_CLEANING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ),
    {
      ...options,
      method: "POST",
    },
  );
};

const DELETE_ONE_RECURRING_JOB = "/v1/rms/recurring_job/remove_job_date/";
export const useDeleteOneScheduledJob = (
  options: ShortMutationConfigType & { organisation_id: string },
) => {
  return useMutation<any, DeleteOneEventProps>(
    DELETE_ONE_RECURRING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ),
    {
      ...options,
      method: "POST",
    },
  );
};

const DELETE_ONE_AND_FOLLOWING_RECURRING_CLEANING_JOB =
  "/v1/organisations/:organisation_id/cleaning_schedule/:task_id/";
export const useDeleteOneAndFollowingScheduledCleaningJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    task_id: string;
  },
) => {
  return useMutation<any, DeleteOneAndFollowingProps>(
    DELETE_ONE_AND_FOLLOWING_RECURRING_CLEANING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ).replace(":task_id", options.task_id),
    {
      ...options,
      method: "PUT",
    },
  );
};

const DELETE_ONE_AND_FOLLOWING_RECURRING_JOB = "v1/rms/recurring_job/:task_id/";
export const useDeleteOneAndFollowingScheduledJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    task_id: string;
  },
) => {
  return useMutation<any, DeleteOneAndFollowingProps>(
    DELETE_ONE_AND_FOLLOWING_RECURRING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ).replace(":task_id", options.task_id),
    {
      ...options,
      method: "PUT",
    },
  );
};

const EDIT_RECURRING_CLEANING_JOB =
  "/v1/organisations/:organisation_id/cleaning_schedule/:task_id/";
export const useEditScheduledCleaningJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    task_id: string;
  },
) => {
  return useMutation<any, EditFormPayload>(
    EDIT_RECURRING_CLEANING_JOB.replace(
      ":organisation_id",
      options.organisation_id,
    ).replace(":task_id", options.task_id),
    {
      ...options,
      method: "PUT",
    },
  );
};

const EDIT_RECURRING_JOB =
  "/v1/rms/recurring_job/:task_id/";
export const useEditScheduledJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    task_id: string;
  },
) => {
  return useMutation<any, EditDeliveryFormProps>(
    EDIT_RECURRING_JOB.replace(":task_id", options.task_id),
    {
      ...options,
      method: "PUT",
    },
  );
};

export const useEditThisAndFollowingScheduledCleaningJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    task_id: string;
  },
) => {
  const [data, setData] = useState<EditFormProps>();
  const [isReadyToContinue, setIsReadyToContinue] = useState(false);
  const { trigger: firstStep, isLoading: isLoadingFirst } =
    useDeleteOneAndFollowingScheduledCleaningJob({
      organisation_id: options.organisation_id,
      task_id: options.task_id,
      onError: options.onError,
      onSuccess: () => {
        setIsReadyToContinue(true);
      },
    });
  const { trigger: secondStep, isLoading: isLoadingSecond } =
    useCreateScheduledCleaningJob({
      ...options,
      organisation_id: options.organisation_id,
    });

  const trigger = (param: EditFormProps, current_timestamp: number) => {
    setData(param);
    setIsReadyToContinue(false);

    if (isLoadingSecond) {
      return;
    }

    firstStep({
      timestamp_end: current_timestamp,
    });
  };

  useEffect(() => {
    if (!data || !isReadyToContinue) {
      return;
    }
    secondStep(data);
  }, [isReadyToContinue, data]);

  return {
    trigger,
    isLoading: isLoadingFirst || isLoadingSecond,
  };
};

export const useEditThisAndFollowingScheduledJob = (
  options: ShortMutationConfigType & {
    organisation_id: string;
    task_id: string;
  },
) => {
  const [data, setData] = useState<EditDeliveryFormProps>();
  const [isReadyToContinue, setIsReadyToContinue] = useState(false);
  const { trigger: firstStep, isLoading: isLoadingFirst } =
    useDeleteOneAndFollowingScheduledJob({
      organisation_id: options.organisation_id,
      task_id: options.task_id,
      onError: options.onError,
      onSuccess: () => {
        setIsReadyToContinue(true);
      },
    });
  const { trigger: secondStep, isLoading: isLoadingSecond } =
    useCreateScheduledJob({
      ...options
    });

  const trigger = (param: EditDeliveryFormProps, current_timestamp: number) => {
    setData(param);
    setIsReadyToContinue(false);

    if (isLoadingSecond) {
      return;
    }

    firstStep({
      timestamp_end: current_timestamp,
    });
  };

  useEffect(() => {
    if (!data || !isReadyToContinue) {
      return;
    }
    secondStep(data);
  }, [isReadyToContinue, data]);

  return {
    trigger,
    isLoading: isLoadingFirst || isLoadingSecond,
  };
};

export const useEditThisScheduledCleaningJob = (
  options: ShortMutationConfigType & { organisation_id: string },
) => {
  const [data, setData] = useState<EditFormProps>();
  const [isReadyToContinue, setIsReadyToContinue] = useState(false);
  const { trigger: firstStep, isLoading: isLoadingFirst } =
    useDeleteOneScheduledCleaningJob({
      organisation_id: options.organisation_id,
      onError: options.onError,
      onSuccess: () => {
        setIsReadyToContinue(true);
      },
    });
  const { trigger: secondStep, isLoading: isLoadingSecond } =
    useCreateScheduledCleaningJob({
      ...options,
      organisation_id: options.organisation_id,
    });

  const trigger = (param: EditFormProps) => {
    setData(param);
    setIsReadyToContinue(false);

    if (isLoadingSecond) {
      return;
    }

    firstStep({
      job_id: param.job_id,
      timestamp: param.timestamp,
    });
  };

  useEffect(() => {
    if (!data || !isReadyToContinue) {
      return;
    }
    secondStep(data);
  }, [isReadyToContinue, data]);

  return {
    trigger,
    isLoading: isLoadingFirst || isLoadingSecond,
  };
};

export const useEditThisScheduledJob = (
  options: ShortMutationConfigType & { organisation_id: string },
) => {
  const [data, setData] = useState<EditDeliveryFormProps>();
  const [isReadyToContinue, setIsReadyToContinue] = useState(false);
  const { trigger: firstStep, isLoading: isLoadingFirst } =
    useDeleteOneScheduledJob({
      organisation_id: options.organisation_id,
      onError: options.onError,
      onSuccess: () => {
        setIsReadyToContinue(true);
      },
    });
  const { trigger: secondStep, isLoading: isLoadingSecond } =
    useCreateScheduledJob({
      ...options
    });

  const trigger = (param: EditDeliveryFormProps) => {
    setData(param);
    setIsReadyToContinue(false);

    if (isLoadingSecond) {
      return;
    }

    firstStep({
      job_id: param.job_id,
      timestamp: param.timestamp,
    });
  };

  useEffect(() => {
    if (!data || !isReadyToContinue) {
      return;
    }
    secondStep(data);
  }, [isReadyToContinue, data]);

  return {
    trigger,
    isLoading: isLoadingFirst || isLoadingSecond,
  };
};